import React, { useEffect, useState } from "react";
import "./InteractiveLabs.css"; // Importing the CSS file
import "./Arcade.css"; // Import the dedicated Arcade CSS
import useSound from "../Sound";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";

// Google Analytics Initialization
const TRACKING_ID = "G-1XD9QJLJGR"; // Replace with your GA4 Measurement ID
ReactGA.initialize(TRACKING_ID);

function Arcade() {
    const { play } = useSound("/ui-click-43196.mp3"); // Local file path
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;
    const navigate = useNavigate(); // Hook to programmatically navigate
    const location = useLocation(); // Hook to track current location
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedSubject, setSelectedSubject] = useState("all");
    const [selectedDifficulty, setSelectedDifficulty] = useState("all");

    // Track page views on component mount & route changes
    useEffect(() => {
        if (location.pathname) {
            ReactGA.send({ hitType: "pageview", page: location.pathname });
        }
    }, [location.pathname]);

    const handleSound = () => {
        play();
    };

    const handleAccess = (path) => {
        play();
        ReactGA.event({
            category: "Navigation",
            action: "Clicked on a Game",
            label: path,
        });
        navigate(path);
    };

    // Comprehensive game data organized by type
    const gameCategories = {
        quiz: {
            title: "Quiz & Knowledge Games",
            description: "Test your knowledge with interactive quizzes and multiplayer challenges",
            icon: "🧠",
            games: [
                {
                    title: "Quick Quiz Game",
                    description: "Challenge your knowledge across multiple subjects with real-time multiplayer",
                    image: "image/quiz.jpg",
                    path: "/QuickQuizGame",
                    difficulty: "Medium",
                    subjects: ["General", "STEM"],
                    features: ["Multiplayer", "Real-time"],
                    transferable: true
                },
                {
                    title: "Live Quiz Sessions",
                    description: "Join or host collaborative quiz sessions with friends",
                    image: "image/live-quiz.jpg",
                    path: "/LiveQuizPage",
                    difficulty: "Medium",
                    subjects: ["General", "STEM"],
                    features: ["Multiplayer", "Collaborative"],
                    transferable: true
                },
                {
                    title: "Fun Quiz Challenge",
                    description: "Engaging quiz system with gamified learning experience",
                    image: "image/fun-quiz.jpg",
                    path: "/FunQuizzes",
                    difficulty: "Easy",
                    subjects: ["General"],
                    features: ["Gamified"],
                    transferable: true
                }
            ]
        },
        science: {
            title: "Science & Research Games",
            description: "Explore advanced scientific concepts through interactive gameplay",
            icon: "🔬",
            games: [
                {
                    title: "Biochemical Mechanisms",
                    description: "Master biochemical pathways and molecular interactions",
                    image: "image/pathways.jpg",
                    path: "/Biochem",
                    difficulty: "Advanced",
                    subjects: ["Biochemistry", "Biology"],
                    features: ["Pathway Visualization"],
                    transferable: false
                },
                {
                    title: "Drug Discovery Quest",
                    description: "Navigate pharmaceutical research and drug development concepts",
                    image: "image/drug-discovery.jpg",
                    path: "/DrugDiscoveryGame",
                    difficulty: "Advanced",
                    subjects: ["Pharmacology", "Chemistry"],
                    features: ["Research Simulation"],
                    transferable: false
                },
                {
                    title: "Dosage Calculator Pro",
                    description: "Master pharmaceutical dosage calculations with real-world scenarios",
                    image: "image/Dosage.png",
                    path: "/DosageGame",
                    difficulty: "Advanced",
                    subjects: ["Pharmacology", "Math"],
                    features: ["Calculator", "Real-world"],
                    transferable: true
                }
            ]
        },
        puzzle: {
            title: "Puzzle & Problem Solving",
            description: "Develop critical thinking through challenging puzzles",
            icon: "🧩",
            games: [
                {
                    title: "IUPAC Nomenclature Master",
                    description: "Master organic chemistry naming conventions step by step",
                    image: "image/nomenclature.png",
                    path: "/nomenclature",
                    difficulty: "Medium",
                    subjects: ["Organic Chemistry"],
                    features: ["Step-by-step", "Interactive"],
                    transferable: false
                },
                {
                    title: "Stereocenter Challenge",
                    description: "Identify R/S configurations in complex molecular structures",
                    image: "image/R_S.png",
                    path: "/stereocentergame",
                    difficulty: "Advanced",
                    subjects: ["Organic Chemistry"],
                    features: ["3D Visualization"],
                    transferable: false
                },
                {
                    title: "Physics Problem Solver",
                    description: "Tackle kinematics problems with guided solutions",
                    image: "image/kinetics.jpg",
                    path: "/Kinematics",
                    difficulty: "Medium",
                    subjects: ["Physics", "Math"],
                    features: ["Step-by-step", "Guided"],
                    transferable: true
                },
                {
                    title: "Electrostatics Explorer",
                    description: "Explore electric fields, forces, and electromagnetic concepts",
                    image: "image/electrostatics.png",
                    path: "/Electrostatics",
                    difficulty: "Advanced",
                    subjects: ["Physics"],
                    features: ["Simulation", "Interactive"],
                    transferable: true
                }
            ]
        },
        action: {
            title: "Action & Adventure Games",
            description: "Learn through exciting adventures and fast-paced challenges",
            icon: "🎮",
            games: [
                {
                    title: "STEM Maze Escape",
                    description: "Navigate through challenging mazes by solving STEM problems",
                    image: "image/maze.jpg",
                    path: "/maze-game",
                    difficulty: "Easy",
                    subjects: ["STEM", "General"],
                    features: ["Adventure", "Problem Solving"],
                    transferable: true
                },
                {
                    title: "STEM Stack Attack",
                    description: "Build towering structures by answering questions correctly",
                    image: "image/stack.jpg",
                    path: "/stack-game",
                    difficulty: "Medium",
                    subjects: ["STEM", "General"],
                    features: ["Strategy", "Building"],
                    transferable: true
                },
                {
                    title: "Balloon Pop Frenzy",
                    description: "Fast-paced balloon popping with correct answer selection",
                    image: "image/balloon.jpg",
                    path: "/balloon-pop",
                    difficulty: "Easy",
                    subjects: ["STEM", "General"],
                    features: ["Fast-paced", "Reaction Time"],
                    transferable: true
                }
            ]
        },
        labs: {
            title: "Interactive Laboratory",
            description: "Hands-on experiments and simulations across all sciences",
            icon: "⚗️",
            games: [
                {
                    title: "Physics Lab Suite",
                    description: "Comprehensive physics simulations covering mechanics, waves, and optics",
                    image: "image/physics-lab.jpg",
                    path: "/InteractiveLabs",
                    difficulty: "Medium",
                    subjects: ["Physics"],
                    features: ["Simulation", "Experiments"],
                    transferable: true
                },
                {
                    title: "Chemistry Lab Explorer",
                    description: "Interactive periodic table, molecular builders, and reaction simulators",
                    image: "image/chem-lab.jpg",
                    path: "/InteractiveLabs",
                    difficulty: "Medium",
                    subjects: ["Chemistry"],
                    features: ["Molecular Builder", "Periodic Table"],
                    transferable: false
                },
                {
                    title: "Math Workshop",
                    description: "Algebra, calculus, and matrix operations with visual representations",
                    image: "image/math-lab.jpg",
                    path: "/InteractiveLabs",
                    difficulty: "Medium",
                    subjects: ["Mathematics"],
                    features: ["Visual Learning", "Calculator"],
                    transferable: true
                },
                {
                    title: "3D Anatomy Explorer",
                    description: "Immersive 3D anatomy models with VR support",
                    image: "image/anatomy.jpg",
                    path: "/anatomy2",
                    difficulty: "Medium",
                    subjects: ["Biology", "Medicine"],
                    features: ["3D", "VR Support"],
                    transferable: false
                },
                {
                    title: "Code Academy",
                    description: "Learn programming with interactive code editor and execution",
                    image: "image/coding.jpg",
                    path: "/code-editor",
                    difficulty: "Medium",
                    subjects: ["Computer Science"],
                    features: ["Code Editor", "Real-time Execution"],
                    transferable: true
                }
            ]
        },
        study: {
            title: "Study & Memory Tools",
            description: "Enhance learning with flashcards, spaced repetition, and progress tracking",
            icon: "📚",
            games: [
                {
                    title: "Flashcard Master",
                    description: "Create and study custom flashcards with XP rewards and statistics",
                    image: "image/flashcards.jpg",
                    path: "/flashcardstudy",
                    difficulty: "Easy",
                    subjects: ["General"],
                    features: ["XP System", "Statistics", "Custom Decks"],
                    transferable: true
                },
                {
                    title: "Assessment Center",
                    description: "Comprehensive testing with adaptive difficulty and detailed analytics",
                    image: "image/assessment.jpg",
                    path: "/assessments",
                    difficulty: "Medium",
                    subjects: ["General"],
                    features: ["Adaptive Testing", "Analytics"],
                    transferable: true
                }
            ]
        }
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case "Easy": return "#4CAF50";
            case "Medium": return "#FF9800";
            case "Advanced": return "#F44336";
            default: return "#2196F3";
        }
    };

    const getAllSubjects = () => {
        const subjects = new Set();
        Object.values(gameCategories).forEach(category => {
            category.games.forEach(game => {
                game.subjects.forEach(subject => subjects.add(subject));
            });
        });
        return Array.from(subjects).sort();
    };

    const getFilteredGames = () => {
        let allGames;
        if (selectedCategory === "all") {
            allGames = Object.values(gameCategories).flatMap(category => 
                category.games.map(game => ({ ...game, categoryTitle: category.title }))
            );
        } else {
            allGames = gameCategories[selectedCategory]?.games || [];
        }

        // Apply subject filter
        if (selectedSubject !== "all") {
            allGames = allGames.filter(game => game.subjects.includes(selectedSubject));
        }

        // Apply difficulty filter
        if (selectedDifficulty !== "all") {
            allGames = allGames.filter(game => game.difficulty === selectedDifficulty);
        }

        return allGames;
    };

    const getGamesByCategory = () => {
        if (selectedSubject === "all" && selectedDifficulty === "all") {
            return gameCategories;
        }
        
        const filteredCategories = {};
        Object.entries(gameCategories).forEach(([key, category]) => {
            const filteredGames = category.games.filter(game => {
                const subjectMatch = selectedSubject === "all" || game.subjects.includes(selectedSubject);
                const difficultyMatch = selectedDifficulty === "all" || game.difficulty === selectedDifficulty;
                return subjectMatch && difficultyMatch;
            });
            
            if (filteredGames.length > 0) {
                filteredCategories[key] = { ...category, games: filteredGames };
            }
        });
        
        return filteredCategories;
    };

    return (
        <div className="outer-lab-container">
            <div className="lab-container">
                <div className="arcade-header">
                    <h1 className="lab-title">🎮 Educational Game Arcade</h1>
                    <p className="arcade-subtitle">
                        Learn through play! Choose from our collection of educational games designed to make learning fun and engaging.
                    </p>
                </div>

                {/* Enhanced Filtering System */}
                <div className="filter-system">
                    {/* Category Filter */}
                    <div className="filter-group">
                        <h3 className="filter-title">Game Type</h3>
                        <div className="filter-buttons">
                            <button 
                                className={`filter-btn ${selectedCategory === "all" ? "active" : ""}`}
                                onClick={() => {setSelectedCategory("all"); handleSound();}}
                            >
                                🎆 All Types
                            </button>
                            {Object.entries(gameCategories).map(([key, category]) => (
                                <button 
                                    key={key}
                                    className={`filter-btn ${selectedCategory === key ? "active" : ""}`}
                                    onClick={() => {setSelectedCategory(key); handleSound();}}
                                >
                                    {category.icon} {category.title}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Subject Filter */}
                    <div className="filter-group">
                        <h3 className="filter-title">Subject</h3>
                        <div className="filter-buttons">
                            <button 
                                className={`filter-btn ${selectedSubject === "all" ? "active" : ""}`}
                                onClick={() => {setSelectedSubject("all"); handleSound();}}
                            >
                                🌍 All Subjects
                            </button>
                            {getAllSubjects().map(subject => (
                                <button 
                                    key={subject}
                                    className={`filter-btn ${selectedSubject === subject ? "active" : ""}`}
                                    onClick={() => {setSelectedSubject(subject); handleSound();}}
                                >
                                    {subject}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Difficulty Filter */}
                    <div className="filter-group">
                        <h3 className="filter-title">Difficulty</h3>
                        <div className="filter-buttons">
                            <button 
                                className={`filter-btn ${selectedDifficulty === "all" ? "active" : ""}`}
                                onClick={() => {setSelectedDifficulty("all"); handleSound();}}
                            >
                                🎯 All Levels
                            </button>
                            {["Easy", "Medium", "Advanced"].map(difficulty => (
                                <button 
                                    key={difficulty}
                                    className={`filter-btn difficulty-${difficulty.toLowerCase()} ${selectedDifficulty === difficulty ? "active" : ""}`}
                                    onClick={() => {setSelectedDifficulty(difficulty); handleSound();}}
                                >
                                    {difficulty}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Game Categories */}
                {selectedCategory === "all" && selectedSubject === "all" && selectedDifficulty === "all" ? (
                    <div className="game-categories">
                        {Object.entries(getGamesByCategory()).map(([key, category]) => (
                            <div key={key} className="game-category">
                                <div className="category-header">
                                    <h2>{category.icon} {category.title}</h2>
                                    <p className="category-description">{category.description}</p>
                                    <span className="game-count">{category.games.length} games</span>
                                </div>
                                <div className="games-grid">
                                    {category.games.map((game, index) => (
                                        <div key={index} className="game-card enhanced" onClick={() => handleAccess(game.path)}>
                                            <div className="game-image-container">
                                                <img
                                                    src={game.image}
                                                    alt={game.title}
                                                    className="game-image"
                                                    onError={(e) => {e.target.onerror = null; e.target.src="image/quiz.jpg"}}
                                                />
                                                <div className="difficulty-badge" style={{backgroundColor: getDifficultyColor(game.difficulty)}}>
                                                    {game.difficulty}
                                                </div>
                                                {game.transferable && (
                                                    <div className="transferable-badge" title="This game can be adapted to multiple subjects">
                                                        🔁
                                                    </div>
                                                )}
                                            </div>
                                            <div className="game-info">
                                                <h3 className="game-title">{game.title}</h3>
                                                <p className="game-description">{game.description}</p>
                                                <div className="game-meta">
                                                    <div className="game-subjects">
                                                        {game.subjects.map((subject, idx) => (
                                                            <span key={idx} className="subject-tag">{subject}</span>
                                                        ))}
                                                    </div>
                                                    {game.features && (
                                                        <div className="game-features">
                                                            {game.features.map((feature, idx) => (
                                                                <span key={idx} className="feature-tag">{feature}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="filtered-games">
                        <div className="results-header">
                            <h2>🎯 Filtered Results</h2>
                            <p className="results-count">{getFilteredGames().length} games found</p>
                        </div>
                        <div className="games-grid">
                            {getFilteredGames().map((game, index) => (
                                <div key={index} className="game-card enhanced" onClick={() => handleAccess(game.path)}>
                                    <div className="game-image-container">
                                        <img
                                            src={game.image}
                                            alt={game.title}
                                            className="game-image"
                                            onError={(e) => {e.target.onerror = null; e.target.src="image/quiz.jpg"}}
                                        />
                                        <div className="difficulty-badge" style={{backgroundColor: getDifficultyColor(game.difficulty)}}>
                                            {game.difficulty}
                                        </div>
                                        {game.transferable && (
                                            <div className="transferable-badge" title="This game can be adapted to multiple subjects">
                                                🔁
                                            </div>
                                        )}
                                        {game.categoryTitle && (
                                            <div className="category-badge">
                                                {game.categoryTitle}
                                            </div>
                                        )}
                                    </div>
                                    <div className="game-info">
                                        <h3 className="game-title">{game.title}</h3>
                                        <p className="game-description">{game.description}</p>
                                        <div className="game-meta">
                                            <div className="game-subjects">
                                                {game.subjects.map((subject, idx) => (
                                                    <span key={idx} className="subject-tag">{subject}</span>
                                                ))}
                                            </div>
                                            {game.features && (
                                                <div className="game-features">
                                                    {game.features.map((feature, idx) => (
                                                        <span key={idx} className="feature-tag">{feature}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Enhanced Game Stats */}
                <div className="arcade-stats-enhanced">
                    <h3 className="stats-title">📊 Platform Statistics</h3>
                    <div className="stats-grid">
                        <div className="stat-item">
                            <span className="stat-number">{Object.values(gameCategories).reduce((total, cat) => total + cat.games.length, 0)}</span>
                            <span className="stat-label">Total Games</span>
                            <span className="stat-icon">🎮</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{Object.keys(gameCategories).length}</span>
                            <span className="stat-label">Game Categories</span>
                            <span className="stat-icon">📁</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{getAllSubjects().length}</span>
                            <span className="stat-label">Subjects Covered</span>
                            <span className="stat-icon">📚</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{Object.values(gameCategories).reduce((total, cat) => total + cat.games.filter(game => game.transferable).length, 0)}</span>
                            <span className="stat-label">Cross-Curricular Games</span>
                            <span className="stat-icon">🔁</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{Object.values(gameCategories).reduce((total, cat) => total + cat.games.filter(game => game.features && game.features.includes('Multiplayer')).length, 0)}</span>
                            <span className="stat-label">Multiplayer Games</span>
                            <span className="stat-icon">👥</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{Object.values(gameCategories).reduce((total, cat) => total + cat.games.filter(game => game.features && (game.features.includes('3D') || game.features.includes('VR Support'))).length, 0)}</span>
                            <span className="stat-label">3D/VR Experiences</span>
                            <span className="stat-icon">🥽</span>
                        </div>
                    </div>
                    
                    <div className="transferability-legend">
                        <h4>🔁 Cross-Curricular Learning</h4>
                        <p>Games marked with 🔁 can be adapted across multiple subjects, allowing you to apply the same gameplay mechanics to different educational content.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Arcade;
